import qs from "qs";
import { FilterDef, HTTPGenericResponse } from "../../constants/FinanceModels";
import {
  ID_FILTER_AMOUNT_FROM,
  ID_FILTER_AMOUNT_TO,
  ID_FILTER_AMOUNT_TYPE,
  ID_FILTER_CUSTOMER_ID,
  ID_FILTER_DUE_DATE_FROM,
  ID_FILTER_DUE_DATE_TO,
  ID_FILTER_INVOICE_DATE_FROM,
  ID_FILTER_INVOICE_DATE_TO,
  ID_FILTER_PAYMENT_MODE,
  ID_FILTER_PAYMENT_TYPE,
  ID_FILTER_SAP_INVOICE_NUMBER,
  ID_FILTER_STATUSES,
  ID_FILTER_PAN_CARD,
  ID_FILTER_CREATIONDATEFROM,
  ID_FILTER_CREATIONDATETO,
  ID_FILTER_MODIFICATIONDATEFROM,
  ID_FILTER_MODIFICATIONDATETO,
  ID_PREAUTH_IS_RELEASED,
  ID_FILTER_STATUSES_OUTSTANDING,
  ID_FILTER_CUSTOMER_NAME,
  ID_FILTER_CUSTOMER_LASTNAME,
  ID_FILTER_COMPANY_NAME,
  ID_FILTER_CORRELATION_NAME,
  ID_FILTER_CORRELATION_ID,
  ID_FILTER_LASTTRANSACTIONDATEFROM,
  ID_FILTER_LASTTRANSACTIONDATETO,
  ID_FILTER_INVOICE_NUMBER,
  ID_FILTER_RELEASED_AMOUNT_FROM,
  ID_FILTER_RELEASED_AMOUNT_TO,
  ID_FILTER_RETRY_MADE,
  ID_FILTER_LEGAL_ENTITIES,
  ID_FILTER_DEALERS,
} from "../../pages/payments/pages/components/filters/PaymentRequestConstants";
import { getFilterValue } from "../../utils/FilterUtils";
import { useApi } from "../Api";
import dayjs from "dayjs";
import { useContext } from "react";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";

export const usePaymentRequestRepository = () => {
  const { GET, PUT } = useApi();

  const PATH = "/finance/payment-request";

  const {
    globalData: { user },
  } = useContext(GlobalContext);
  const allLegalEntities: any[] = user.legalEntities
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => x.name);

  const DATE_FIELDS = [
    ID_FILTER_DUE_DATE_FROM,
    ID_FILTER_DUE_DATE_TO,
    ID_FILTER_INVOICE_DATE_FROM,
    ID_FILTER_INVOICE_DATE_TO,
    ID_FILTER_CREATIONDATEFROM,
    ID_FILTER_CREATIONDATETO,
    ID_FILTER_MODIFICATIONDATEFROM,
    ID_FILTER_MODIFICATIONDATETO,
    ID_FILTER_LASTTRANSACTIONDATEFROM,
    ID_FILTER_LASTTRANSACTIONDATETO,
  ];
  const OTHER_FIELDS = [
    ID_FILTER_AMOUNT_TYPE,
    ID_FILTER_CUSTOMER_ID,
    ID_FILTER_PAYMENT_TYPE,
    ID_FILTER_SAP_INVOICE_NUMBER,
    ID_FILTER_PAYMENT_MODE,
    ID_FILTER_STATUSES,
    ID_FILTER_PAN_CARD,
    ID_PREAUTH_IS_RELEASED,
    ID_FILTER_CUSTOMER_NAME,
    ID_FILTER_CUSTOMER_LASTNAME,
    ID_FILTER_COMPANY_NAME,
    ID_FILTER_LEGAL_ENTITIES,
    ID_FILTER_DEALERS,
  ];

  const outStandingDefaultValues = [
    "OUTSTANDING",
    "SUSPENDED",
    "EXTERNAL_MANAGEMENT",
    "ACCOUNTED",
  ];

  async function getPaymentsRequests(
    offset: number,
    limit: number,
    sort: string | null,
    order: string | null,
    filterDefs: FilterDef[] = [],
    customer: string | null,
    invoiceNr: string | null,
    correlationName: string | null,
    correlationId: string | null,
    correlationIdStrict: boolean | null,
    outstanding: boolean | null
  ): Promise<HTTPGenericResponse> {
    let type = getFilterValue(ID_FILTER_PAYMENT_TYPE, filterDefs);
    let amountType = getFilterValue(ID_FILTER_AMOUNT_TYPE, filterDefs);
    let customerId = customer
      ? customer
      : getFilterValue(ID_FILTER_CUSTOMER_ID, filterDefs);
    let sapInvoiceNumber = getFilterValue(
      ID_FILTER_SAP_INVOICE_NUMBER,
      filterDefs
    );
    let statuses = outstanding
      ? getFilterValue(ID_FILTER_STATUSES_OUTSTANDING, filterDefs) === undefined
        ? outStandingDefaultValues
        : getFilterValue(ID_FILTER_STATUSES_OUTSTANDING, filterDefs)
      : getFilterValue(ID_FILTER_STATUSES, filterDefs);
    let invoiceDateFrom: Date = getFilterValue(
      ID_FILTER_INVOICE_DATE_FROM,
      filterDefs
    );
    let invoiceDateTo: Date = getFilterValue(
      ID_FILTER_INVOICE_DATE_TO,
      filterDefs
    );
    let fromDueDate: Date = getFilterValue(ID_FILTER_DUE_DATE_FROM, filterDefs);
    let toDueDate: Date = getFilterValue(ID_FILTER_DUE_DATE_TO, filterDefs);
    let fromAmount: number = getFilterValue(ID_FILTER_AMOUNT_FROM, filterDefs);
    let toAmount: number = getFilterValue(ID_FILTER_AMOUNT_TO, filterDefs);
    let paymentMode = getFilterValue(ID_FILTER_PAYMENT_MODE, filterDefs);
    let panCard = getFilterValue(ID_FILTER_PAN_CARD, filterDefs);
    let fromCreationDate: Date = getFilterValue(
      ID_FILTER_CREATIONDATEFROM,
      filterDefs
    );
    let toCreationDate: Date = getFilterValue(
      ID_FILTER_CREATIONDATETO,
      filterDefs
    );
    let fromModificationDate: Date = getFilterValue(
      ID_FILTER_MODIFICATIONDATEFROM,
      filterDefs
    );
    let toModificationDate: Date = getFilterValue(
      ID_FILTER_MODIFICATIONDATETO,
      filterDefs
    );
    let preauthIsReleased = getFilterValue(ID_PREAUTH_IS_RELEASED, filterDefs);
    let customerName = getFilterValue(ID_FILTER_CUSTOMER_NAME, filterDefs);
    let customerLastName = getFilterValue(
      ID_FILTER_CUSTOMER_LASTNAME,
      filterDefs
    );
    let companyName = getFilterValue(ID_FILTER_COMPANY_NAME, filterDefs);
    let alreadySentToSap = outstanding ? false : null;
    let fromTransactionDate: Date = getFilterValue(
      ID_FILTER_LASTTRANSACTIONDATEFROM,
      filterDefs
    );
    let toTransactionDate: Date = getFilterValue(
      ID_FILTER_LASTTRANSACTIONDATETO,
      filterDefs
    );
    let correlationNameParameter = correlationName
      ? correlationName
      : getFilterValue(ID_FILTER_CORRELATION_NAME, filterDefs);
    let correlationIdParameter = correlationId
      ? correlationId
      : getFilterValue(ID_FILTER_CORRELATION_ID, filterDefs);

    let invoiceNumber = invoiceNr
      ? invoiceNr
      : getFilterValue(ID_FILTER_INVOICE_NUMBER, filterDefs);
    let releasedAmountFrom: number = getFilterValue(
      ID_FILTER_RELEASED_AMOUNT_FROM,
      filterDefs
    );
    let releasedAmountTo: number = getFilterValue(
      ID_FILTER_RELEASED_AMOUNT_TO,
      filterDefs
    );
    let retryMade = getFilterValue(ID_FILTER_RETRY_MADE, filterDefs);
    let legalEntities: string[] =
      getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) || allLegalEntities;
    let dealers: string[] = getFilterValue(ID_FILTER_DEALERS, filterDefs);

    let params = {
      offset: offset,
      limit: limit,
      sortBy: sort,
      order: order,
      amountType: amountType,
      fromAmount: fromAmount ? fromAmount * 100 : fromAmount,
      toAmount: toAmount ? toAmount * 100 : toAmount,
      type: type,
      status: statuses,
      maskeredPan: panCard,
      fromInvoiceDate: invoiceDateFrom
        ? dayjs(invoiceDateFrom).toISOString()
        : null,
      toInvoiceDate: invoiceDateTo ? dayjs(invoiceDateTo).toISOString() : null,
      fromDueDate: fromDueDate ? dayjs(fromDueDate).toISOString() : null,
      toDueDate: toDueDate ? dayjs(toDueDate).toISOString() : null,
      fromCreatedDate: fromCreationDate
        ? dayjs(fromCreationDate).toISOString()
        : null,
      toCreatedDate: toCreationDate
        ? dayjs(toCreationDate).toISOString()
        : null,
      fromModifiedDate: fromModificationDate
        ? dayjs(fromModificationDate).toISOString()
        : null,
      toModifiedDate: toModificationDate
        ? dayjs(toModificationDate).toISOString()
        : null,
      sapInvoiceNumber: sapInvoiceNumber,
      customerId: customerId,
      cardTerminalType: paymentMode,
      preauthIsReleased: preauthIsReleased ? preauthIsReleased : null,
      customerName: customerName,
      customerLastName: customerLastName,
      customerRagioneSociale: companyName,
      alreadySentToSap: alreadySentToSap,
      correlationName: correlationNameParameter,
      correlationId: correlationIdParameter,
      isOutstanding: outstanding ? true : null,
      fromTransactionDate: fromTransactionDate
        ? dayjs(fromTransactionDate).toISOString()
        : null,
      toTransactionDate: toTransactionDate
        ? dayjs(toTransactionDate).toISOString()
        : null,
      invoiceNumber: invoiceNumber,
      releasedAmountFrom: releasedAmountFrom
        ? releasedAmountFrom * 100
        : releasedAmountFrom,
      releasedAmountTo: releasedAmountTo
        ? releasedAmountTo * 100
        : releasedAmountTo,
      retryMade: retryMade,
      correlationIdStrict: correlationIdParameter ? correlationIdStrict : null,
      dealers,
      legalEntities,
    };

    let response = await GET(`${PATH}`, {
      params: params,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", skipNulls: true }),
    });
    return new Promise((resolve) => {
      let generic: HTTPGenericResponse = {
        code: 200,
        status: "OK",
        message: "",
        result: response,
      };
      resolve(generic);
    });
  }

  function getSummaryRequest(
    filterDefs: FilterDef[] = [],
    outstanding: boolean | null
  ): Promise<HTTPGenericResponse> {
    let params: any = {};
    let legalEntities: string[] =
      getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) || allLegalEntities;
    let dealers: string[] = getFilterValue(ID_FILTER_DEALERS, filterDefs);

    DATE_FIELDS.forEach((p) => {
      let date: Date = getFilterValue(p, filterDefs);
      params[p] = date ? dayjs(date).toISOString() : null;
    });
    OTHER_FIELDS.forEach((p) => {
      params[p] = getFilterValue(p, filterDefs);
    });
    params["correlationName"] = getFilterValue(
      ID_FILTER_CORRELATION_NAME,
      filterDefs
    );
    params["correlationId"] = getFilterValue(
      ID_FILTER_CORRELATION_ID,
      filterDefs
    );
    params["type"] = getFilterValue(ID_FILTER_PAYMENT_TYPE, filterDefs);
    params["fromAmount"] = getFilterValue(ID_FILTER_AMOUNT_FROM, filterDefs);
    params["toAmount"] = getFilterValue(ID_FILTER_AMOUNT_TO, filterDefs);
    params["fromAmount"] = params["fromAmount"]
      ? params["fromAmount"] * 100
      : params["fromAmount"];
    params["toAmount"] = params["toAmount"]
      ? params["toAmount"] * 100
      : params["toAmount"];
    params["cardTerminalType"] = getFilterValue(
      ID_FILTER_PAYMENT_MODE,
      filterDefs
    );
    params["isOutstanding"] = outstanding ? true : null;
    params["alreadySentToSap"] = outstanding ? false : null;
    params["invoiceNumber"] = getFilterValue(
      ID_FILTER_INVOICE_NUMBER,
      filterDefs
    );
    params["releasedAmountFrom"] = params["releasedAmountFrom"]
      ? params["releasedAmountFrom"] * 100
      : params["releasedAmountFrom"];
    params["releasedAmountTo"] = params["releasedAmountTo"]
      ? params["releasedAmountTo"] * 100
      : params["releasedAmountTo"];
    params["retryMade"] = getFilterValue(ID_FILTER_RETRY_MADE, filterDefs);
    params["legalEntities"] = legalEntities;
    params["dealers"] = dealers;

    return GET(`${PATH}/statuses`, {
      params: params,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", skipNulls: true }),
    });
  }

  async function getPaymentsRequestsById(
    id: string
  ): Promise<HTTPGenericResponse> {
    const params = {
      legalEntities: allLegalEntities,
    };
    let response = await GET(`${PATH}/` + id, {
      params: params,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", skipNulls: true }),
    });

    return new Promise((resolve) => {
      let generic: HTTPGenericResponse = {
        code: 200,
        status: "OK",
        message: "",
        result: response,
      };
      resolve(generic);
    });
  }

  async function putPaymentsRequestsById(
    id: string,
    status: string
  ): Promise<HTTPGenericResponse> {
    let response = await PUT(`${PATH}/` + id + `/` + status, {});
    return new Promise((resolve) => {
      let generic: HTTPGenericResponse = {
        code: 200,
        status: "OK",
        message: "",
        result: response,
      };
      resolve(generic);
    });
  }

  async function putNoteById(
    id: string,
    note: string
  ): Promise<HTTPGenericResponse> {
    let response = await PUT(`${PATH}/` + id + `/note`, note, {}, true);
    return new Promise((resolve) => {
      let generic: HTTPGenericResponse = {
        code: 200,
        status: "OK",
        message: "",
        result: response,
      };
      resolve(generic);
    });
  }

  async function putAmountById(
    id: string,
    amount: string
  ): Promise<HTTPGenericResponse> {
    let response = await PUT(
      `${PATH}/` + id + `/amount-recovered`,
      amount,
      {},
      true
    );
    return new Promise((resolve) => {
      let generic: HTTPGenericResponse = {
        code: 200,
        status: "OK",
        message: "",
        result: response,
      };
      resolve(generic);
    });
  }

  function downloadXML(id: string) {
    return GET(`${PATH}/${id}/xml`);
  }

  async function getUnpaidPaymentHistory(
    id: string
  ): Promise<HTTPGenericResponse> {
    const params = {
      legalEntities: allLegalEntities,
    };

    let response = await GET(
      `${PATH}/` + id + "/unpaid-history",
      {
        params: params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat", skipNulls: true }),
      },
      false
    );
    return new Promise((resolve) => {
      let generic: HTTPGenericResponse = {
        code: 200,
        status: "OK",
        message: "",
        result: response,
      };
      resolve(generic);
    });
  }

  async function putOutstandingByIds(
    payload: string[],
    status: string
  ): Promise<HTTPGenericResponse> {
    let response = await PUT(`${PATH}/` + status, payload);
    return new Promise((resolve) => {
      let generic: HTTPGenericResponse = {
        code: 200,
        status: "OK",
        message: "",
        result: response,
      };
      resolve(generic);
    });
  }

  async function putOutstandingNoteByIds(
    payload: any
  ): Promise<HTTPGenericResponse> {
    let response = await PUT(`${PATH}/note`, payload);
    return new Promise((resolve) => {
      let generic: HTTPGenericResponse = {
        code: 200,
        status: "OK",
        message: "",
        result: response,
      };
      resolve(generic);
    });
  }

  function downloadInvoice(id: string) {
    const params = {
      legalEntities: allLegalEntities,
    };

    return GET(`${PATH}/${id}/invoice/download`, {
      params: params,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat", skipNulls: true }),
    });
  }

  return {
    getPaymentsRequests,
    getSummaryRequest,
    getPaymentsRequestsById,
    putPaymentsRequestsById,
    downloadXML,
    putNoteById,
    putAmountById,
    getUnpaidPaymentHistory,
    putOutstandingByIds,
    putOutstandingNoteByIds,
    downloadInvoice,
  };
};
